import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, X, ChevronLeft, ChevronRight, Maximize2, Home, Upload, Zap, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Unique Alphanumeric reference code generator
const getProductRefCode = (product) => {
  if (!product) return '';
  const cleanId = product.id.toString().replace(/[^a-zA-Z0-9]/g, '').slice(-6).toUpperCase();
  return `EE-PRD-${cleanId}`;
};

const ProductCard = ({ product, onZoom, onContactRequest }) => {
  const imageUrls = product.image_url ? product.image_url.split(',').map(x => x.trim()).filter(Boolean) : [];
  const [activeIdx, setActiveIdx] = useState(0);
  const refCode = getProductRefCode(product);

  return (
    <motion.div
      variants={itemVariants}
      id={`product-card-${product.id}`}
      data-testid={`product-${product.id}`}
      className="bg-[#121212] border border-[#27272A] hover:border-[#FF9900] transition-all duration-300 overflow-hidden group flex flex-col justify-between rounded-sm"
    >
      <div>
        {/* Image Frame */}
        <div 
          onClick={() => onZoom && onZoom(activeIdx)}
          className="relative h-80 overflow-hidden bg-[#0A0A0A] cursor-zoom-in"
        >
          <img
            src={imageUrls[activeIdx] || '/images/placeholder.jpg'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {imageUrls.length > 0 && (
            <div className="absolute top-3 left-3 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 border border-white/10 rounded-full flex items-center justify-center text-white z-10 shadow-md hover:scale-110">
              <Maximize2 className="w-4 h-4 text-[#FF9900]" />
            </div>
          )}
          {imageUrls.length > 1 && (
            <span className="absolute top-3 right-3 bg-black/80 text-[#FF9900] text-[10px] font-bold px-2.5 py-1 border border-[#FF9900]/30 rounded-full tracking-wider uppercase z-10">
              {imageUrls.length} Photos
            </span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
        </div>

        {/* Thumbnail Selector Row if multiple images */}
        {imageUrls.length > 1 && (
          <div className="flex gap-2 p-3 bg-[#0D0D0D] border-b border-[#27272A] overflow-x-auto">
            {imageUrls.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`w-12 h-12 flex-shrink-0 border-2 overflow-hidden transition-all duration-200 ${
                  activeIdx === idx ? 'border-[#FF9900] scale-95' : 'border-[#27272A] opacity-65 hover:opacity-100'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-6 flex flex-col justify-between flex-grow">
          <div>
            <div className="flex justify-between items-start gap-4 mb-3">
              <h3 className="text-2xl font-bold text-[#F3F4F6] uppercase tracking-tight leading-snug">{product.name}</h3>
              <span className="text-[9px] text-[#FF9900] font-black uppercase tracking-wider bg-[#FF9900]/10 border border-[#FF9900]/25 px-2 py-0.5 rounded-sm flex-shrink-0">
                {refCode}
              </span>
            </div>
            <p className="text-gray-400 leading-relaxed text-sm whitespace-pre-line mb-4">{product.description}</p>
            {product.production_time && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Production Time:</span>
                <span className="text-xs font-bold text-[#FF9900] bg-[#FF9900]/10 border border-[#FF9900]/20 px-2.5 py-0.5 rounded-sm">
                  {product.production_time} {parseInt(product.production_time) === 1 ? 'Day' : 'Days'}
                </span>
              </div>
            )}
          </div>
          <button 
            type="button"
            onClick={() => onContactRequest && onContactRequest(product)}
            className="w-full inline-block bg-[#1E1E1E] text-[#FF9900] border border-[#FF9900]/30 hover:border-[#FF9900] hover:bg-[#FF9900] hover:text-black transition-all px-4 py-2.5 text-xs uppercase font-bold tracking-wider text-center"
          >
            Request This Build
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Lightbox States
  const [lightboxProduct, setLightboxProduct] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // Contact Popup Form States
  const [contactProduct, setContactProduct] = useState(null);
  const [contactData, setContactData] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    urgency: 'flexible',
    message: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  // Hash anchor scrolling and detailed focus view
  useEffect(() => {
    if (loading || products.length === 0) return;

    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith('#EE-PRD-')) {
        const refCode = hash.replace('#', '');
        const matchedProduct = products.find(p => getProductRefCode(p) === refCode);
        if (matchedProduct) {
          // Open in detailed focus (lightbox) mode immediately!
          setLightboxProduct(matchedProduct);
          setLightboxIdx(0);

          // Scroll to and highlight card in the background
          setTimeout(() => {
            const element = document.getElementById(`product-card-${matchedProduct.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('border-[#FF9900]', 'ring-4', 'ring-[#FF9900]/25');
              setTimeout(() => {
                element.classList.remove('ring-4', 'ring-[#FF9900]/25');
              }, 3000);
            }
          }, 300);
        }
      }
    };

    handleHashChange();

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [loading, products]);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const lightboxImages = lightboxProduct && lightboxProduct.image_url
    ? lightboxProduct.image_url.split(',').map(x => x.trim()).filter(Boolean)
    : [];

  const handleNext = () => {
    if (lightboxImages.length > 1) {
      setLightboxIdx((prev) => (prev + 1) % lightboxImages.length);
    }
  };

  const handlePrev = () => {
    if (lightboxImages.length > 1) {
      setLightboxIdx((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    }
  };

  // Keyboard navigation listener
  useEffect(() => {
    if (!lightboxProduct) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxProduct(null);
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxProduct, lightboxImages.length]);

  // Drag and drop event handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files);
    const totalSize = [...selectedFiles, ...newFiles].reduce((acc, curr) => acc + curr.size, 0);
    
    if (totalSize > 10 * 1024 * 1024) {
      setFieldErrors(prev => ({...prev, files: "Total file size exceeds 10MB limit."}));
      return;
    }
    
    setFieldErrors(prev => ({...prev, files: ""}));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetContactForm = () => {
    setContactData({
      full_name: '',
      company: '',
      email: '',
      phone: '',
      urgency: 'flexible',
      message: ''
    });
    setSelectedFiles([]);
    setFieldErrors({});
    setSubmitError('');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    const errors = {};
    if (!contactData.full_name) errors.full_name = "Name is required.";
    if (!contactData.email) errors.email = "Email is required.";
    if (!contactData.phone) errors.phone = "Phone number is required.";
    if (!contactData.message) errors.message = "Message is required.";
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append('full_name', contactData.full_name);
    formData.append('company', contactData.company);
    formData.append('email', contactData.email);
    formData.append('phone', contactData.phone);
    formData.append('urgency', contactData.urgency);
    formData.append('source_page', `products_referred_${getProductRefCode(contactProduct)}`);
    
    const refCode = getProductRefCode(contactProduct);
    const productLink = `${window.location.origin}/products#${refCode}`;
    const injectedMessage = `[REFERRED BUILD INTEREST: ${contactProduct.name} (Ref Code: ${refCode})]\nDirect Catalog Link: ${productLink}\n--------------------------------------------------\n${contactData.message}`;
    formData.append('message', injectedMessage);

    selectedFiles.forEach((file) => {
      formData.append('drawings[]', file);
    });

    try {
      const response = await axios.post(`${API}/contact-submissions.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success || response.status === 200) {
        setSubmitSuccess(true);
        resetContactForm();
        setTimeout(() => {
          setSubmitSuccess(false);
          setContactProduct(null);
        }, 2500);
      } else {
        setSubmitError(response.data.detail || 'Failed to submit form.');
      }
    } catch (err) {
      console.error("Failed to submit contact form", err);
      setSubmitError(err.response?.data?.detail || 'An error occurred while submitting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 pb-12 md:py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          {/* Desktop Back Button */}
          <Link
            to="/"
            data-testid="back-to-home"
            className="hidden md:inline-flex items-center gap-2 text-[#FF9900] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium uppercase">Back to Home</span>
          </Link>

          {/* Mobile Back Button: Home Icon in Top Right Corner */}
          <Link
            to="/"
            data-testid="back-to-home-mobile"
            className="md:hidden fixed top-3 right-3 z-50 bg-[#1E1E1E] border border-[#27272A] p-3 text-[#FF9900] hover:bg-[#FF9900] hover:text-black transition-all duration-300 rounded-sm shadow-md flex items-center justify-center"
            title="Back to Home"
          >
            <Home className="w-6 h-6" />
          </Link>

          <h1 className="text-5xl sm:text-6xl font-bold text-[#FF9900] mb-6 font-black uppercase">OUR PRODUCTS</h1>
          <p className="text-gray-400 text-lg max-w-3xl leading-relaxed">
            Browse our complete catalog of custom-fabricated products. Each item is built to customer specifications
            for the agriculture and oil industries.
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-16 h-16 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4 font-bold uppercase tracking-wider text-xs">Loading products...</p>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onZoom={(initialIdx) => {
                  setLightboxProduct(product);
                  setLightboxIdx(initialIdx);
                }}
                onContactRequest={(p) => setContactProduct(p)}
              />
            ))}
          </motion.div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20 border border-[#27272A] bg-[#121212] rounded-sm p-8 max-w-xl mx-auto">
            <p className="text-gray-400 text-lg uppercase font-bold tracking-wide">No products available at the moment.</p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 p-8 bg-[#121212] border-l-4 border-[#FF9900] rounded-sm border border-[#27272A]"
        >
          <h3 className="text-2xl font-bold text-[#F3F4F6] mb-4 uppercase">Custom Orders Welcome</h3>
          <p className="text-gray-400 text-lg mb-6 leading-relaxed">
            Don't see what you're looking for? We specialize in custom fabrication. Contact us to discuss your project.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 bg-[#FF9900] text-black px-6 py-3.5 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300"
          >
            Go to Contact Form
          </Link>
        </motion.div>
      </div>

      {/* Restructured Fullscreen Product Detail Lightbox */}
      <AnimatePresence>
        {lightboxProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-4 md:p-8 select-none"
            onClick={() => setLightboxProduct(null)}
          >
            <div 
              className="relative bg-[#121212] border border-[#27272A] w-full max-w-6xl h-[85vh] lg:h-[75vh] flex flex-col lg:flex-row overflow-hidden shadow-2xl rounded-sm"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left Column: Image Slider */}
              <div className="relative w-full lg:w-3/5 h-1/2 lg:h-full bg-[#070707] flex flex-col justify-between p-4 border-b lg:border-b-0 lg:border-r border-[#27272A] overflow-hidden select-none">
                {/* Arrow chevrons and active frame */}
                <div className="relative flex-grow flex items-center justify-center overflow-hidden my-auto">
                  {lightboxImages.length > 1 && (
                    <button
                      type="button"
                      onClick={handlePrev}
                      className="absolute left-2 z-10 p-2.5 bg-black/60 border border-white/10 hover:border-[#FF9900] hover:bg-[#FF9900] hover:text-black text-white transition-all rounded-full flex items-center justify-center shadow-lg"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                  )}

                  <motion.div
                    key={lightboxIdx}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.2 }}
                    className="max-w-full max-h-[30vh] lg:max-h-[55vh] flex items-center justify-center"
                  >
                    <img
                      src={lightboxImages[lightboxIdx] || '/images/placeholder.jpg'}
                      alt={`${lightboxProduct.name} - ${lightboxIdx + 1}`}
                      className="max-w-full max-h-[30vh] lg:max-h-[55vh] object-contain rounded-sm border border-[#27272A]/50 shadow-xl"
                    />
                  </motion.div>

                  {lightboxImages.length > 1 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="absolute right-2 z-10 p-2.5 bg-black/60 border border-white/10 hover:border-[#FF9900] hover:bg-[#FF9900] hover:text-black text-white transition-all rounded-full flex items-center justify-center shadow-lg"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Slideshow metadata strip */}
                <div className="w-full text-center space-y-2 mt-2 shrink-0">
                  {lightboxImages.length > 1 && (
                    <p className="text-[#FF9900] font-black text-[10px] uppercase tracking-widest">
                      Photo {lightboxIdx + 1} of {lightboxImages.length}
                    </p>
                  )}
                  {lightboxImages.length > 1 && (
                    <div className="flex justify-center gap-1.5 overflow-x-auto py-1 px-2 max-w-md mx-auto">
                      {lightboxImages.map((img, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setLightboxIdx(idx)}
                          className={`w-10 h-10 flex-shrink-0 border overflow-hidden transition-all duration-200 ${
                            lightboxIdx === idx ? 'border-[#FF9900] scale-95' : 'border-[#27272A] opacity-50 hover:opacity-100'
                          }`}
                        >
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Complete details sheet */}
              <div className="w-full lg:w-2/5 h-1/2 lg:h-full bg-[#121212] flex flex-col justify-between p-6 md:p-8 overflow-y-auto">
                <div className="space-y-6">
                  {/* Top Header */}
                  <div className="border-b border-[#27272A] pb-4">
                    <div className="flex justify-between items-start gap-4">
                      <h2 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight leading-snug">
                        {lightboxProduct.name}
                      </h2>
                      <button
                        type="button"
                        onClick={() => setLightboxProduct(null)}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                        title="Close Gallery"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                    <span className="text-[10px] text-[#FF9900] font-black uppercase tracking-widest block mt-2 bg-[#FF9900]/10 border border-[#FF9900]/25 px-2.5 py-1 rounded-sm w-fit">
                      {getProductRefCode(lightboxProduct)}
                    </span>
                  </div>

                  {/* Specifications & Description */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Description & Specifications</h3>
                    <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                      {lightboxProduct.description || "No customized instructions listed. Engineered and fabricated strictly to customer plans and specifications."}
                    </p>
                  </div>

                  {/* Estimated Production Days Timeline */}
                  {lightboxProduct.production_time && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Estimated Fabrication Time</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-bold text-[#FF9900] bg-[#FF9900]/10 border border-[#FF9900]/20 px-3.5 py-1 rounded-sm">
                          {lightboxProduct.production_time} {parseInt(lightboxProduct.production_time) === 1 ? 'Day' : 'Days'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Primary Request Build Call to Action Button */}
                <div className="pt-6 border-t border-[#27272A] mt-6 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setContactProduct(lightboxProduct);
                      setLightboxProduct(null);
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-[#FF9900] text-black py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 shadow-xl text-sm"
                  >
                    Request This Build
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Glassmorphic Contact Submission Popup Modal */}
      <AnimatePresence>
        {contactProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 md:p-6 overflow-y-auto select-none"
            onClick={() => setContactProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#1E1E1E] border border-[#27272A] w-full max-w-2xl max-h-[90vh] flex flex-col justify-between shadow-2xl rounded-sm overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center border-b border-[#27272A] p-6 shrink-0">
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-[#F3F4F6] uppercase tracking-wide">Request This Build</h3>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Eastend Iron Industries</p>
                </div>
                <button
                  type="button"
                  onClick={() => setContactProduct(null)}
                  className="text-gray-400 hover:text-white transition-colors p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form or Success State */}
              {submitSuccess ? (
                <div className="p-12 text-center flex flex-col items-center justify-center gap-4 flex-grow bg-[#121212]/30">
                  <div className="w-16 h-16 bg-green-950/40 border border-green-850 text-[#FF9900] flex items-center justify-center rounded-full animate-bounce">
                    <Check className="w-8 h-8 text-[#FF9900]" />
                  </div>
                  <h4 className="text-lg font-bold text-white uppercase tracking-wider">Message Sent Successfully!</h4>
                  <p className="text-gray-400 text-sm max-w-sm">
                    George and our estimating team will review your inquiry and follow up within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="p-6 overflow-y-auto space-y-6">
                  {/* Visual Build Reference Alert Block */}
                  <div className="bg-[#121212] border border-[#FF9900]/25 rounded-sm p-4 flex gap-4 items-center">
                    <div className="w-16 h-10 bg-[#070707] border border-[#27272A] overflow-hidden shrink-0 rounded-sm">
                      <img 
                        src={contactProduct.image_url ? contactProduct.image_url.split(',')[0].trim() : '/images/placeholder.jpg'} 
                        alt={contactProduct.name} 
                        className="w-full h-full object-cover" 
                      />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Referred Build Interest</h4>
                      <h3 className="text-sm font-bold text-white uppercase mt-0.5">{contactProduct.name}</h3>
                      <span className="text-[9px] font-black text-[#FF9900] uppercase tracking-wider bg-[#FF9900]/10 border border-[#FF9900]/20 px-2.5 py-0.5 rounded-sm mt-1 inline-block">
                        {getProductRefCode(contactProduct)}
                      </span>
                    </div>
                  </div>

                  {submitError && (
                    <div className="p-3 bg-red-950/40 border border-red-500/50 text-red-200 text-xs flex items-center gap-2 rounded-sm">
                      <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                      <div>{submitError}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Full Name & Company */}
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={contactData.full_name}
                        onChange={(e) => { setContactData({...contactData, full_name: e.target.value}); setFieldErrors(prev => ({...prev, full_name: ''})); }}
                        className={`w-full bg-[#121212] border ${fieldErrors.full_name ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm`}
                      />
                      {fieldErrors.full_name && <p className="text-red-400 text-xs mt-1">{fieldErrors.full_name}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Company</label>
                      <input
                        type="text"
                        value={contactData.company}
                        onChange={(e) => setContactData({...contactData, company: e.target.value})}
                        className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm"
                      />
                    </div>

                    {/* Email & Phone */}
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={contactData.email}
                        onChange={(e) => { setContactData({...contactData, email: e.target.value}); setFieldErrors(prev => ({...prev, email: ''})); }}
                        className={`w-full bg-[#121212] border ${fieldErrors.email ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm`}
                      />
                      {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Phone *</label>
                      <input
                        type="tel"
                        required
                        pattern="[\d\s\+\-\(\)\.]{8,25}"
                        title="Please enter a valid phone number format"
                        value={contactData.phone}
                        onChange={(e) => { setContactData({...contactData, phone: e.target.value}); setFieldErrors(prev => ({...prev, phone: ''})); }}
                        className={`w-full bg-[#121212] border ${fieldErrors.phone ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm`}
                      />
                      {fieldErrors.phone && <p className="text-red-400 text-xs mt-1">{fieldErrors.phone}</p>}
                    </div>

                    {/* Urgency */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Timeline / Urgency *</label>
                      <select
                        required
                        value={contactData.urgency}
                        onChange={(e) => setContactData({...contactData, urgency: e.target.value})}
                        className="w-full bg-[#121212] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm"
                      >
                        <option value="flexible">Flexible / General Inquiry</option>
                        <option value="2-4_weeks">2 - 4 weeks</option>
                        <option value="1-2_weeks">1 - 2 weeks</option>
                        <option value="within_this_week">Within this week</option>
                        <option value="asap">As soon as possible</option>
                      </select>
                      {contactData.urgency === 'asap' && (
                        <div className="mt-3 p-3 bg-red-950/40 border border-red-500/50 text-red-200 text-[11px] flex items-start gap-2 animate-pulse">
                          <Zap className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-bold">EMERGENCY:</span> For immediate dispatch, call George directly at <a href="tel:4032232620" className="font-bold underline hover:text-[#FF9900]">(403) 223-2620</a>!
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Message */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Message *</label>
                      <textarea
                        required
                        rows={4}
                        value={contactData.message}
                        onChange={(e) => { setContactData({...contactData, message: e.target.value}); setFieldErrors(prev => ({...prev, message: ''})); }}
                        className={`w-full bg-[#121212] border ${fieldErrors.message ? 'border-red-500' : 'border-[#27272A]'} text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors text-sm`}
                        placeholder="Please describe any custom specs or blueprints you would like George to review."
                      ></textarea>
                      {fieldErrors.message && <p className="text-red-400 text-xs mt-1">{fieldErrors.message}</p>}
                    </div>

                    {/* Files Attachment */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Attach Drawings, Sketched Blueprints, or Images (Optional)</label>
                      <div
                        onDragEnter={handleDrag}
                        onDragOver={handleDrag}
                        onDragLeave={handleDrag}
                        onDrop={handleDrop}
                        className={`w-full bg-[#121212] border-2 border-dashed ${dragActive ? 'border-[#FF9900] bg-[#FF9900]/5' : 'border-[#27272A]'} p-4 transition-all duration-300 text-center relative group rounded-sm`}
                      >
                        <input
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.webp"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <Upload className="w-8 h-8 text-gray-500 group-hover:text-[#FF9900] mx-auto mb-2 transition-colors duration-300" />
                        <p className="text-[#F3F4F6] text-xs font-semibold mb-1">
                          Drag and drop files here, or <span className="text-[#FF9900] hover:underline">browse files</span>
                        </p>
                        <p className="text-gray-500 text-[10px]">
                          Supports PDF, JPG, PNG, WEBP, DWG, DXF up to 10MB total
                        </p>
                      </div>

                      {selectedFiles.length > 0 && (
                        <div className="mt-3 space-y-1.5 max-h-[150px] overflow-y-auto custom-scrollbar">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="bg-[#121212] border border-[#27272A] p-2 flex items-center justify-between text-[11px] rounded-sm animate-fadeIn">
                              <span className="text-gray-300 truncate pr-4">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-red-400 hover:text-red-300 transition-colors p-1"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Actions */}
                  <div className="border-t border-[#27272A] pt-4 flex gap-4 justify-end">
                    <button
                      type="button"
                      onClick={() => setContactProduct(null)}
                      className="px-5 py-2.5 bg-[#121212] hover:bg-[#27272A] text-gray-400 font-bold uppercase text-xs tracking-wider rounded-sm transition-colors border border-[#27272A]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2.5 bg-[#FF9900] text-black font-bold uppercase text-xs tracking-wider hover:bg-white transition-colors flex items-center gap-2 rounded-sm disabled:opacity-50"
                    >
                      {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {submitting ? 'Submitting...' : 'Send Inquiry'}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductsPage;